import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useI18n } from '../i18n'
import { Button } from './ui/Button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorDisplayProps {
  error: Error | null
  errorInfo: ErrorInfo | null
  onRetry: () => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, errorInfo, onRetry }) => {
  const { t } = useI18n()
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600" />
        </div>
        
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t('error.somethingWentWrong')}
          </h2>
          
          <p className="text-gray-600 mb-4">
            {t('error.unexpectedError')}
          </p>

          {process.env.NODE_ENV === 'development' && error && (
            <details className="text-left mb-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                {t('error.details')}
              </summary>
              <div className="bg-gray-100 p-3 rounded text-xs font-mono text-gray-800 overflow-auto max-h-32">
                <div className="font-semibold text-red-600 mb-1">
                  {error.name}: {error.message}
                </div>
                <div className="whitespace-pre-wrap">
                  {error.stack}
                </div>
                {errorInfo && (
                  <div className="mt-2 pt-2 border-t border-gray-300">
                    <div className="font-semibold text-gray-600 mb-1">{t('error.componentStack')}</div>
                    <div className="whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </div>
                  </div>
                )}
              </div>
            </details>
          )}

          <div className="flex gap-3 justify-center">
            <Button
              type="button"
              color="primary"
              onClick={onRetry}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              icon={<RefreshCw className="w-4 h-4" />}
            >
              {t('error.tryAgain')}
            </Button>

            <Button
              type="button"
              color="secondary"
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              {t('error.refreshPage')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    })

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI with translations
      return (
        <ErrorDisplay 
          error={this.state.error} 
          errorInfo={this.state.errorInfo} 
          onRetry={this.handleRetry} 
        />
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary